import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import * as Sortable from '@givebutter/primitives/sortable';
import { useToast } from '@givebutter/primitives/toast';
import EmojiPicker from '@emoji-mart/react';
import {
    faCheck,
    faEdit,
    faEyeSlash,
    faLink,
    faPlus,
    faSmile,
    faTrash,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { createAuctionCategory, updateAuctionCategory } from 'api/campaigns';
import * as Form from 'components/form';
import * as DeleteAuctionCategoryDialog from 'components/pages/campaign-settings/campaign-auction/auction-categories/DeleteAuctionCategoryDialog';
import { useDeleteAuctionCategoryMutation } from 'components/pages/campaign-settings/campaign-auction/auction-categories/useDeleteAuctionCategoryMutation';
import { useCopy } from 'components/snippet';
import * as Tooltip from 'components/tooltip';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { getSlugFromGeneric } from 'helpers/formatting';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { AuctionCategory } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AddAuctionCategoryFormValues {
    icon: string | null;
    name: string;
    slug: string;
    description: string | null;
    isPublished: boolean;
}

interface AuctionCategoryFormProps {
    category?: AuctionCategory;
}

const AuctionCategoryForm = (props: AuctionCategoryFormProps) => {
    const { category } = props;
    const toast = useToast();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;
    const auctionId = campaign?.auction_id!;
    const { didCopy, onClickCopy } = useCopy({ inPortal: true });
    const [count, setCount] = useState(0);
    const { mutate: deleteAuctionCategory } = useDeleteAuctionCategoryMutation({});
    const methods = Form.useGivebutterForm<AddAuctionCategoryFormValues>({
        defaultValues: {
            icon: category ? category.icon : null,
            name: category ? category.name : '',
            slug: category ? category.slug : '',
            description: category ? category.description : '',
            isPublished: category ? category.is_published : true,
        },
    });
    const { control, watch, setValue, getValues, resetFormButton, path, reset } = methods;
    const [icon, name, slug] = watch(['icon', 'name', 'slug']);

    const { mutate: submit } = useMutation({
        mutationFn: () => {
            const formValues = transformValuesToRequest(getValues());
            return category
                ? updateAuctionCategory(campaignId, auctionId, category.id, formValues)
                : createAuctionCategory(campaignId, auctionId, formValues);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionCategories(campaignId, auctionId),
            });
            setCount(count => count + 1);
            reset();
            toast.success('Category saved');
        },
        onError: (error: GivebutterAxiosError) => {
            resetFormButton();
            toast.error(<>{error.response?.data.message ?? 'Unknown error occurred'}</>);
        },
    });

    const onChangeSlug = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue('slug', getSlugFromGeneric(e.target.value));
    };

    const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (slug.length === 0 || slug === getSlugFromGeneric(name)) {
            setValue('slug', getSlugFromGeneric(value));
        }
        setValue('name', value);
    };

    return (
        <Collapsible.Root
            key={count}
            className={`group flex items-center gap-4 ${!category ? 'flex-col' : ''}`}
        >
            {category && (
                <div className="group-radix-state-open:self-start min-w-[1.25rem] text-gray-600">
                    {category?.listNumber ? (
                        `${category.listNumber}.`
                    ) : (
                        <FontAwesomeIcon icon={faEyeSlash} className="text-gray-300" />
                    )}
                </div>
            )}
            {category && (
                <Sortable.Item
                    itemId={category?.id ?? 0}
                    className={
                        'data-[dragging=true]:z-outermost group-radix-state-open:hidden flex-grow focus:outline-2 focus:outline-blue-500'
                    }
                >
                    <div className="flex items-center gap-2 text-sm">
                        <Sortable.Card className="h-10">
                            <Sortable.Grip />
                            {!!category?.icon && (
                                <span className={!category?.is_published ? 'opacity-30' : ''}>
                                    {category?.icon}
                                </span>
                            )}
                            <span
                                className={`font-medium ${
                                    !category?.is_published ? 'text-gray-300' : 'text-gray-900'
                                }`}
                            >
                                {category?.name}
                            </span>
                            {!category?.is_published && (
                                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium">
                                    Hidden
                                </span>
                            )}
                            <div className="flex flex-grow justify-end opacity-0 group-hover:opacity-100">
                                {!category.is_published && (
                                    <Tooltip.Root>
                                        <Tooltip.Trigger asChild>
                                            <Button
                                                variant="transparent"
                                                shape="square"
                                                onClick={e => {
                                                    onClickCopy(
                                                        e,
                                                        `${campaign?.url}/auction/categories/${category.id}/${category.slug}`,
                                                    );
                                                }}
                                                aria-label="Copy"
                                            >
                                                <FontAwesomeIcon
                                                    icon={didCopy ? faCheck : faLink}
                                                    className="text-gray-500 hover:text-gray-800"
                                                />
                                            </Button>
                                        </Tooltip.Trigger>
                                        <Tooltip.Content inverse>
                                            <div>
                                                Copy link to category page. Only those with the link
                                                will be able to see your items.
                                                <Tooltip.Arrow inverse />
                                            </div>
                                        </Tooltip.Content>
                                    </Tooltip.Root>
                                )}
                                <Collapsible.Trigger asChild>
                                    <Button
                                        variant="transparent"
                                        shape={'square'}
                                        aria-label="Edit"
                                    >
                                        <FontAwesomeIcon
                                            icon={faEdit}
                                            className="text-gray-500 hover:text-gray-800"
                                        />
                                    </Button>
                                </Collapsible.Trigger>

                                <DeleteAuctionCategoryDialog.Root
                                    hasItems={category.has_items}
                                    onDelete={() => deleteAuctionCategory(category?.id ?? 0)}
                                >
                                    <DeleteAuctionCategoryDialog.Trigger asChild>
                                        <Button
                                            variant="transparent"
                                            shape="square"
                                            aria-label="Delete"
                                        >
                                            <FontAwesomeIcon
                                                icon={faXmark}
                                                className="text-gray-500 hover:text-gray-800"
                                            />
                                        </Button>
                                    </DeleteAuctionCategoryDialog.Trigger>
                                </DeleteAuctionCategoryDialog.Root>
                            </div>
                        </Sortable.Card>
                    </div>
                </Sortable.Item>
            )}
            <Collapsible.Content>
                <div
                    className={`bg-gray-25 flex flex-col gap-6 rounded border border-gray-200 p-6 shadow-sm`}
                >
                    <Form.Container>
                        <Form.Label name={path('name')}>
                            Category label
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot control={control} name={path('name')}>
                            <Popover.Root>
                                <Popover.Trigger asChild={true}>
                                    <div className="flex h-8 w-4 cursor-pointer items-center">
                                        {icon || (
                                            <FontAwesomeIcon
                                                icon={faSmile}
                                                className="text-gray-400"
                                            />
                                        )}
                                    </div>
                                </Popover.Trigger>
                                <Popover.Portal>
                                    <Popover.Content>
                                        <EmojiPicker
                                            theme="light"
                                            onEmojiSelect={({ native }: { native: string }) => {
                                                setValue('icon', native);
                                            }}
                                        />
                                    </Popover.Content>
                                </Popover.Portal>
                            </Popover.Root>
                            <Form.TextInputForm
                                placeholder={'Category label'}
                                onClick={e => e.preventDefault()}
                                onChange={onChangeName}
                            />
                        </Form.TextInputRoot>
                        <p className="text-sm text-gray-600">Shown as the category page title.</p>
                    </Form.Container>
                    <Form.Container>
                        <Form.Label name={path('slug')}>
                            URL slug
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot control={control} name={path('slug')}>
                            <Form.TextInputForm
                                placeholder={'category-label'}
                                onChange={onChangeSlug}
                            />
                        </Form.TextInputRoot>
                        <p className="text-sm text-gray-600">
                            Shown in the web address when viewing this category.
                        </p>
                    </Form.Container>
                    <Form.Container>
                        <Form.Label name={path('description')}>Description</Form.Label>
                        <Form.UseFormTextArea
                            name={path('description')}
                            placeholder="Enter description"
                            control={control}
                        />
                        <p className="text-sm text-gray-600">
                            Shown beneath the category page title.
                        </p>
                    </Form.Container>
                    <Form.UseFormContainer className={'flex flex-col gap-y-1'}>
                        <div className={'flex justify-between'}>
                            <div className={'flex flex-col gap-y-1'}>
                                <Form.UseFormLabel name={path('isPublished')}>
                                    Make visible in the auction
                                </Form.UseFormLabel>
                                <p className={'text-sm text-gray-600'}>
                                    Make this category and its items visible to bidders on the
                                    auction page. If hidden, you can copy a direct link to the
                                    category page and its items and share selectively.
                                </p>
                            </div>
                            <Form.Switch name={path('isPublished')} control={control} />
                        </div>
                    </Form.UseFormContainer>
                    <div className="flex justify-end gap-2">
                        {category ? (
                            <DeleteAuctionCategoryDialog.Root
                                hasItems={category.has_items}
                                onDelete={() => deleteAuctionCategory(category.id)}
                            >
                                <DeleteAuctionCategoryDialog.Trigger asChild>
                                    <Button
                                        variant="transparent"
                                        size="sm"
                                        shape="square"
                                        aria-label="Delete"
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="text-gray-600" />
                                    </Button>
                                </DeleteAuctionCategoryDialog.Trigger>
                            </DeleteAuctionCategoryDialog.Root>
                        ) : (
                            <Collapsible.Trigger asChild>
                                <Button
                                    variant="transparent"
                                    size="sm"
                                    shape="square"
                                    aria-label="Delete"
                                >
                                    <FontAwesomeIcon icon={faTrash} className="text-gray-600" />
                                </Button>
                            </Collapsible.Trigger>
                        )}
                        <Collapsible.Trigger asChild>
                            <Button variant="outline" color="neutral" size="sm">
                                Cancel
                            </Button>
                        </Collapsible.Trigger>
                        <Button variant="solid" size="sm" onClick={() => submit()}>
                            Save category
                        </Button>
                    </div>
                </div>
            </Collapsible.Content>
            {!category && (
                <Collapsible.Trigger asChild>
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start ring-gray-200"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New category
                    </Button>
                </Collapsible.Trigger>
            )}
        </Collapsible.Root>
    );
};

const transformValuesToRequest = (values: AddAuctionCategoryFormValues) => {
    return {
        icon: values.icon === '' ? null : values.icon,
        name: values.name,
        slug: values.slug,
        description: values.description === '' ? null : values.description,
        is_published: values.isPublished,
    };
};

export type { AddAuctionCategoryFormValues };
export { AuctionCategoryForm };
